import { Badge, Button, Card, cn, FIELD, FIELD_INVALID, Label } from '@routly/ui';
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Sign-in for the admin console, composed exactly as the product's is: the same
 * ambient backdrop, the same brand header, the same rounded card, the same
 * fields and the same reveal button.
 *
 * It had been a 384px box with 44px inputs and a 6px radius while the product
 * used a 28px card with 48px inputs and a 16px radius — the sort of difference
 * nobody can name and everybody notices. The two are separate applications, so
 * the shared package carries what can be shared (the components, the field
 * styling) and this file repeats the composition.
 *
 * The copy is English rather than translated: the console has no i18n, and one
 * untranslated string is better than a translation layer nobody maintains.
 */

/** Better Auth's default floor, and what the server will reject below. */
const MIN_PASSWORD = 8;

/**
 * Deliberately loose. The server decides whether an address exists; this only
 * catches the typo you can see without asking it.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-x-0 top-0 h-64 bg-linear-to-b from-secondary to-transparent" />
      <div className="absolute -left-24 top-14 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
    </div>
  );
}

export function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ email: false, password: false });
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const emailError = !email
    ? 'Enter your email'
    : !EMAIL_SHAPE.test(email)
      ? 'That does not look like an email address'
      : null;
  const passwordError = !password
    ? 'Enter your password'
    : password.length < MIN_PASSWORD
      ? `Passwords are at least ${MIN_PASSWORD} characters`
      : null;

  const showEmailError = touched.email && emailError;
  const showPasswordError = touched.password && passwordError;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched({ email: true, password: true });
    if (emailError || passwordError) return;

    setErrorMsg(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (caught) {
      setErrorMsg(caught instanceof Error ? caught.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-y-auto bg-background text-foreground">
      <h1 className="sr-only">Sign in to the Routly admin console</h1>
      <AmbientBackdrop />

      <div className="relative mx-auto flex min-h-screen w-full max-w-2xl flex-col px-4 py-6 sm:px-6">
        <div className="flex items-center gap-2.5 pb-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary">
            <ShieldCheck className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="text-lg font-bold text-foreground">Routly</span>
        </div>

        <div className="flex flex-1 items-center">
          <Card className="w-full gap-0 overflow-hidden rounded-xl bg-card/95 shadow-lg backdrop-blur-xs">
            <div className="space-y-5 border-b border-border px-6 py-6 sm:px-8">
              <Badge variant="accent">
                <ShieldCheck />
                Admin console
              </Badge>
              <div className="space-y-2">
                <h2 className="text-3xl font-semibold tracking-tight text-foreground text-balance">
                  Routly Admin
                </h2>
                <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
                  Accounts, organizations and the health of everything behind the product.
                </p>
              </div>
            </div>

            <div className="space-y-6 px-6 py-6 sm:px-8">
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                    aria-invalid={showEmailError ? true : undefined}
                    aria-describedby={showEmailError ? 'email-error' : undefined}
                    className={cn(FIELD, showEmailError && FIELD_INVALID)}
                    placeholder="you@company.com"
                  />
                  {showEmailError ? (
                    <p id="email-error" className="text-xs text-destructive">
                      {emailError}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <input
                      id="password"
                      type={revealed ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
                      aria-invalid={showPasswordError ? true : undefined}
                      aria-describedby={showPasswordError ? 'password-error' : undefined}
                      className={cn(FIELD, 'pr-12', showPasswordError && FIELD_INVALID)}
                      placeholder="••••••••"
                    />
                    {/*
                      `type="button"`, or it submits the form on the first click.
                      Not disabled while signing in either: the one moment you
                      most want to check what you typed is after it was refused.
                    */}
                    <button
                      type="button"
                      onClick={() => setRevealed((shown) => !shown)}
                      aria-label={revealed ? 'Hide password' : 'Show password'}
                      aria-pressed={revealed}
                      className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-2xl text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {showPasswordError ? (
                    <p id="password-error" className="text-xs text-destructive">
                      {passwordError}
                    </p>
                  ) : null}
                </div>

                {errorMsg ? (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                ) : null}

                <Button
                  className="flex h-12 w-full items-center justify-center gap-2"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {busy ? 'Signing in…' : 'Sign in'}
                  {busy ? null : <ArrowRight className="h-4 w-4" />}
                </Button>
              </form>

              <p className="text-xs leading-relaxed text-muted-foreground">
                The console is limited to super administrators. Accounts are created by another
                administrator — there is no sign-up.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
